import {
  fetchBaseQuery,
  FetchBaseQueryError,
  BaseQueryFn,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../index';
import {
  getExtendedToken,
  hasValidExtendedToken,
  getUserProfileWithAccessToken,
  refreshExtendedToken,
  resetAppAuthService,
} from '../../services/appAuthService';
import { performLogout } from '../../services/logoutService';
import { buildApiUrl } from '@/config/api.config';
import { STORAGE_KEYS } from '../../utils/storageKeys';

/**
 * Utility: Wait until extended token is available
 */
async function waitForExtendedToken(timeout = 5000, interval = 100): Promise<string | null> {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    const token = getExtendedToken() || localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) return token;
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  console.warn('⏳ Token was not available within timeout — proceeding without it');
  return null;
}

/**
 * Custom baseQuery wrapper
 */
export const createExtendedTokenBaseQuery = (
  baseUrl: string
): BaseQueryFn<any, unknown, FetchBaseQueryError> => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: `${buildApiUrl(baseUrl)}`,
    prepareHeaders: (headers, { getState }) => {
      let token = getExtendedToken();

      if (!token) {
        token = (getState() as RootState).auth.accessToken;
      }
      if (!token) {
        token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      }

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      } else {
        console.warn('⚠️ No token available for API call');
      }

      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    // Skip authentication handling on invitation page or when invitation params are present
    const params = new URLSearchParams(window.location.search);
    const hasInvitationParams = params.has('invitation') && params.has('organization');
    if (window.location.pathname === '/invite' || hasInvitationParams) {
      return rawBaseQuery(args, api, extraOptions);
    }

    // Step 1: Wait for a token before making API request
    if (!hasValidExtendedToken()) {
      const stateToken =
        (api.getState() as RootState).auth.accessToken ||
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (stateToken) {
        // Get user profile and refresh extended token if needed
        await getUserProfileWithAccessToken(stateToken);
      } else {
        // Check if we're in initial auth phase - be more patient
        const isInitialAuth = sessionStorage.getItem(STORAGE_KEYS.INITIAL_AUTH_PHASE) === 'true';
        const timeout = isInitialAuth ? 10000 : 5000; // 10s for initial auth, 5s otherwise
        
        // Wait until something sets the token
        const token = await waitForExtendedToken(timeout);
        if (!token) {
          // During initial auth, return a more specific error
          const errorMessage = isInitialAuth
            ? 'Authentication in progress. Please wait...'
            : 'No authentication token available';
            
          return {
            error: {
              status: 401,
              data: { message: errorMessage },
            } as FetchBaseQueryError,
            meta: {
              baseQueryMeta: {
                response: { status: 401 }
              }
            }
          };
        }
      }
    }

    let result = await rawBaseQuery(args, api, extraOptions);

    // Step 2: Handle expired/invalid token (401)
    if (result.error && (result.error as FetchBaseQueryError).status === 401) {
      console.warn('🔄 Token invalid — trying to refresh…');

      try {
        const stateToken =
          (api.getState() as RootState).auth.accessToken ||
          localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

        if (stateToken) {
          const refreshed = await refreshExtendedToken(stateToken);
          if (refreshed?.access_token) {
            console.info('✅ Token refreshed — retrying request');
            result = await rawBaseQuery(args, api, extraOptions);
          } else {
            console.error('❌ Refresh failed — logging out');
            resetAppAuthService();
            performLogout();
          }
        } else {
          console.error('❌ No base token available — logging out');
          resetAppAuthService();
          performLogout();
        }
      } catch (err) {
        console.error('❌ Token refresh error:', err);
        resetAppAuthService();
        performLogout();
      }
    }

    return result;
  };
};
